<template>
  <BaseModal modal-content-class="modal-levels locked">
    <div class="banner_top">
      <h1>{{ $_translations.value.modal_level_level }} {{ level }}</h1>

      <h2>{{ $_translations.value[levelName] }}</h2>
    </div>

    <div class="content">
      <div class="ach_modal_center">
        <BaseImage :src="levelImage" alt="Level" />
      </div>

      <div class="modal_levels_text">
        <p>
          {{ $_translations.value[levelDescription.pt1] }}
          <span class="yellow_bold">{{ totalThreshold }}</span>
          {{ $_translations.value[levelDescription.pt2] }}
        </p>

        <p>
          {{ $_translations.value[levelDescription.pt3] }}
          <span class="yellow_bold">{{ prizeValue }}</span>
          {{ $_translations.value[levelDescription.pt4] }}
        </p>
      </div>

      <div class="flex-row">
        <BaseProgressBar :value="percentageCompleted" />

        <p class="progress_numb">{{ totalCompleted }} / {{ totalThreshold }}</p>
      </div>


      <BaseButton
        v-if="authStore.hasUserAvailableGames"
        @click:button="$emit('start:game')"
      >
        {{ $_translations.value.modal_level_button_play }}
      </BaseButton>

      <BaseButton
        v-else-if="!appSettingsStore.isStoreDisabled"
        @click:button="$emit('redirect:storePage')"
      >
        {{ $_translations.value.modal_level_button_get_more_games }}
      </BaseButton>
    </div>
  </BaseModal>
</template>

<script setup>
import { computed } from 'vue';
import BaseModal from '@/components/modal/BaseModal.vue';
import BaseImage from '@/components/image/BaseImage.vue';
import BaseProgressBar from '@/components/bar/BaseProgressBar.vue';
import BaseButton from '@/components/button/BaseButton.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import {
  calculateLevelCompletedPercentage,
  getLevelNameTranslationKey,
  getLevelNumberImage,
} from '@/helpers/reward.js';
import { lockedImage, unlockedImage } from '@/constants/image.js';

defineEmits([
  'start:game',
  'redirect:storePage',
]);

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
});

const appSettingsStore = useAppSettingsStore();
const authStore = useAuthStore();

const level = computed(() => props.data?.__level);

const totalThreshold = computed(() => props.data?.threshold ?? 0);

const prizeValue = computed(() => props.data?.prizeValue ?? '');

const levelName = computed(() => getLevelNameTranslationKey(level.value));

const isNextLevel = computed(() => (authStore.user.level + 1) === level.value);

const isCompleted = computed(() => totalThreshold.value <= authStore.user.gamesPlayed);

const isLocked = computed(() => !isCompleted.value && !isNextLevel.value);

const levelImage = computed(() => {
  if (isNextLevel.value) {
    return getLevelNumberImage(level.value);
  } else {
    return isLocked.value ? lockedImage : unlockedImage;
  }
});

const levelDescription = computed(() => {
  let type;

  if (isNextLevel.value) {
    type = 'next';
  } else {
    type = isLocked.value ? 'locked' : 'unlocked';
  }

  return {
    pt1: `modal_level_${ type }_description_pt1`,
    pt2: `modal_level_${ type }_description_pt2`,
    pt3: `modal_level_${ type }_description_pt3`,
    pt4: `modal_level_${ type }_description_pt4`,
  };
});

const totalCompleted = computed(() => isCompleted.value ? totalThreshold.value : authStore.user.gamesPlayed);

const percentageCompleted = computed(() => calculateLevelCompletedPercentage(isCompleted.value, totalCompleted.value, totalThreshold.value));
</script>
